import { getMonthNumber } from '@e2m-customer-portal/shared';
import { getTime, roundToNearestHours } from 'date-fns';
import { clamp } from 'ramda';
import { MonthlyMarketValue } from '../../../../generated/graphql';

export interface SpotPriceResult {
  /**
   * One of twelve color indexes, with zero being green and eleven being red.
   */
  gradient?: number;

  /**
   * The spot price of the forecast.
   */
  price?: number;
}

export function getSpotPrice(
  spotPriceMap: Map<number, number>,
  date: Date,
  monthlyMarketValues?: readonly MonthlyMarketValue[],
): SpotPriceResult {
  const price = spotPriceMap.get(getTime(roundToNearestHours(date, { roundingMethod: 'floor' })));
  if (price === undefined) {
    return {};
  }

  const monthlyMarketValue = monthlyMarketValues?.find((value) => getMonthNumber(value.month) === date.getMonth());

  return { gradient: getSpotPriceGradient(price, monthlyMarketValue), price };
}

export function getSpotPriceGradient(price?: number, monthlyMarketValue?: MonthlyMarketValue) {
  if (price === undefined || !monthlyMarketValue) return;

  const buckets = 10;
  const lowerBuckets = Math.floor(buckets / 2);
  const upperBuckets = Math.ceil(buckets / 2);

  const { min, max, mean } = monthlyMarketValue;

  if (price < 0) {
    return 11;
  } else if (price > mean) {
    // price is above average and positive
    const normalized = (clamp(mean, max, price) - mean) / (max - mean);
    return lowerBuckets - Math.ceil(normalized * lowerBuckets);
  } else {
    // price is below average
    const normalized = (clamp(min, mean, price) - min) / (mean - min);
    return upperBuckets - Math.ceil(normalized * upperBuckets) + lowerBuckets;
  }
}
